import cx from 'classnames'
import { useRef, useState } from 'react'
import Styles from './ExportModal.module.scss'
import ExportStyles from './Export.module.scss'
import { Modal, StackModalProps, useModals } from 'almanac-ui/Modal'
import { useAuthOrUnregisteredUser } from 'hooks/useAuthOrUnregisteredUser'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { ArticleMeta } from 'types'
import { Export, ExportOption } from './Export'
import { Exporter } from './Exporter'
import { ExportFormatEnum, ExportViewState, PdfPaperSize } from './Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button'
import { Images } from 'blaze-ui/icons/24'
import { useExportToZip } from './hooks/useExportToZip'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import { useCreateExportSync } from 'hooks/queries/useCreateExportSync'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

interface ExportModalProps extends StackModalProps {
  article: Pick<ArticleMeta, 'id' | 'title' | 'company_id' | 'channel'>
  onCancel: () => void
  onSuccess: () => void
  backButtonLocation?: 'footer' | 'header'
}

export const ExportToFile = ({ onCancel, article }: ExportModalProps) => {
  const websocketConnection = useWebsocketConnection()
  const { user: auth } = useAuthOrUnregisteredUser()
  const { isChannelSocialPostable } = useIsChannelPostable()
  const [exportViewState, setExportViewState] = useState<ExportViewState>({
    format: isChannelSocialPostable(article.channel) ? ExportFormatEnum.Zip : ExportFormatEnum.Html,
    pdfPaperSize: PdfPaperSize.Letter,
  })
  const { isMobile } = useResponsiveDesign()
  const { closeAllModals } = useModals()
  const { exportToZip } = useExportToZip(article.id)
  const abortExport = useRef(new AbortController())
  const { mutate: exportFile } = useCreateExportSync<{ dismissToast: () => void }>({
    onMutate: () => {
      const dismissToast = showToastMessage(
        'Exporting your file...',
        'loading',
        {
          variant: 'cancel',
          label: 'Cancel',
          type: 'button',
          onPress: () => abortExport.current.abort(),
        },
        0,
      )
      return { dismissToast }
    },
    onSuccess: (data, _, { dismissToast }) => {
      dismissToast()
      if (data.file_url) {
        showToastMessage(
          'File Exported!',
          'success',
          {
            variant: 'proceed',
            label: 'Download',
            type: 'link',
            to: data.file_url,
          },
          10,
          undefined,
        )
      }

      if (!data.file_url) {
        showToastMessage('Export failed', 'error', undefined, 10)
      }
    },
    onError: (error, _, context) => {
      const { dismissToast = () => {} } = context || {}
      dismissToast()
      if (!(error instanceof DOMException) || error.name !== 'AbortError') {
        showToastMessage('Export failed', 'error', undefined, 10)
      }
    },
  })

  const exporter = new Exporter({ article, websocketConnection })

  if (!auth?.id) return null

  const exportButtonText = () => {
    if (exportViewState.format === ExportFormatEnum.Zip) {
      return 'Export Post Content'
    }

    // Fix type
    const viewStateMapToExportAction: any = {
      [ExportFormatEnum.Html]: 'HTML',
      [ExportFormatEnum.Markdown]: 'Markdown',
      [ExportFormatEnum.Pdf]: 'PDF',
    }

    return `Generate ${viewStateMapToExportAction[exportViewState.format]}`
  }

  const handleNewExport = (format: ExportFormatEnum.Pdf | ExportFormatEnum.Html) => {
    closeAllModals()

    exportFile({
      almanacFileId: article.id,
      exportFormat: format,
      pageFormat: exportViewState.pdfPaperSize,
      signal: abortExport.current.signal,
    })
  }

  const onExport = async () => {
    if (exportViewState.format === ExportFormatEnum.Zip) {
      const result = await exportToZip()

      if (result) {
        onCancel()
        closeAllModals()
      }
      return
    }

    if (exportViewState.format === ExportFormatEnum.Pdf || exportViewState.format === ExportFormatEnum.Html) {
      handleNewExport(exportViewState.format)
      return
    }

    exporter.flowCanceller = exporter.requestExport({
      exportOptions: exportViewState,
    })
    onCancel()
    closeAllModals()
    exporter.flowCanceller.catch(() => {}) // Canceller rejects this promise upon cancellation
  }

  return (
    <Modal.Root size="sm">
      <div className={Styles.contentHeader}>
        {!isMobile && <Modal.BackButton onPress={onCancel} />}
        <Modal.Header
          title="Export to file"
          className={Styles.noLeftPadding}
          onClose={onCancel}
          actions={
            <Button variant="ghost" onPress={closeAllModals}>
              Cancel
            </Button>
          }
        />
      </div>
      {isChannelSocialPostable(article.channel) && (
        <div className={cx(Styles.postTextContentContainer, Styles.postContent, ExportStyles.contentWrapper)}>
          <div className={Styles.subHeader}>
            {isMobile ? <div>Export the post content</div> : <Heading level={5}>Export the post content</Heading>}
          </div>
          <Modal.Content>
            <ExportOption
              logo={<Images size={24} color="var(--dark-60)" />}
              title="Post text & image content"
              subTitle="Download a .zip archive containing the post images in JPG format and a text file with the copy."
              selected={exportViewState.format === ExportFormatEnum.Zip}
              onSelect={() => setExportViewState({ ...exportViewState, format: ExportFormatEnum.Zip })}
            />
          </Modal.Content>
        </div>
      )}
      <div className={Styles.subHeader}>
        {isMobile ? <div>Select a file type</div> : <Heading level={5}>Select a file type</Heading>}
      </div>
      <div className={cx({ [Styles.isMobile]: isMobile })}>
        <Export
          close={onCancel}
          exportViewState={exportViewState}
          setExportViewState={setExportViewState}
          onExport={onExport}
        />
      </div>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          {!isMobile && (
            <Button onPress={onCancel} variant="tertiary" size="lg">
              Cancel
            </Button>
          )}
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button
            onPress={onExport}
            size={isMobile ? 'xl' : 'lg'}
            id="export_modal_export_btn"
            data-testid="export_modal_export_btn"
          >
            {exportButtonText()}
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
